import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import {
  Store, QrCode, Palette, Package, FileText, BellRing, Smartphone,
  ShieldCheck, Sparkles, ArrowRight, Check, Upload, Zap,
  MessageSquare, Users, CreditCard, Clock, Archive, History,
} from 'lucide-react';

const FEATURES = [
  { icon: Store, title: 'Your store, live in minutes', desc: 'Sign up and your branded storefront is instantly online at saanjh.cc/store/your-name — no website, no code, no setup fees.' },
  { icon: QrCode, title: 'Your own QR code', desc: 'Every store gets a unique QR code. Print it for your counter, table tents or flyers — customers scan and shop. It updates automatically if you rename your store.' },
  { icon: Palette, title: 'Make it truly yours', desc: 'Customize colours, background, fonts, logo, intro text and currency with a live preview. Looks great on every phone, out of the box.' },
  { icon: Package, title: 'Effortless products', desc: 'Add items one by one or bulk-upload a whole catalogue by CSV. Images, prices, stock and 13 categories from Food & Beverage to Fashion.' },
  { icon: FileText, title: 'Custom pages', desc: 'Tell your story — add About, Menu or info pages with a simple rich-text editor, linked right inside your store.' },
  { icon: BellRing, title: 'Orders, sorted', desc: 'Get an email and a push notification the moment an order arrives. Track every order from your dashboard, and customers receive an order confirmation too.' },
  { icon: MessageSquare, title: 'WhatsApp order alerts', desc: 'Opt in to WhatsApp notifications and get instant order alerts on your phone — and automatic confirmations or cancellations straight to your customers.' },
  { icon: Users, title: 'Team & staff access', desc: 'Add staff members to your dashboard with role-based permissions. They can manage orders or products without touching your account settings.' },
  { icon: CreditCard, title: 'Flexible payment options', desc: 'Accept online UPI payments or let customers pay at the counter. Customers can upload a payment screenshot as proof — you stay in full control.' },
  { icon: History, title: 'Full activity log', desc: 'Every action in your store is tracked — who updated a product, changed a status, or managed the team. A complete audit trail, always at hand.' },
];

const STEPS = [
  { n: '1', title: 'Create your store', desc: 'Sign up free, add your products and brand it your way — all in a few minutes.' },
  { n: '2', title: 'Share your QR & link', desc: 'Print your auto-generated QR code or share your store link anywhere.' },
  { n: '3', title: 'Customers order', desc: 'They browse your mobile storefront, add to cart and place an order in seconds — you’re notified instantly.' },
];

const BUILT_FOR = ['Cafés & restaurants', 'Food trucks', 'Home bakers', 'Cloud kitchens', 'Fashion boutiques', 'Furniture & decor', 'Grocery & retail', 'Pop-up stalls'];

const FAQ = [
  { q: 'Do my customers need an app?', a: 'No. They scan your QR code or tap your link and it opens in any phone browser — no download, no account needed.' },
  { q: 'Do I need a website or tech skills?', a: 'None at all. You get a ready-made store with live, point-and-click customization. If you can fill a form, you can run your store.' },
  { q: 'How do customers check out?', a: 'They add items to a cart and place their order with a quick one-time email code. You get an instant email and confirm/fulfil the order — perfect for pickup, dine-in or local delivery.' },
  { q: 'What payment methods can I accept?', a: 'You choose. Customers can pay online via UPI (with a payment QR or UPI ID you set), or select "pay at counter" and optionally upload a screenshot as proof. You stay in control of when to confirm each order.' },
  { q: 'How do I get notified of new orders?', a: 'You get an email and a browser push notification the moment an order is placed. You can also enable WhatsApp alerts (via the Sikka add-on) so you\'re never more than a buzz away.' },
  { q: 'Can I add my staff to the dashboard?', a: 'Yes. You can invite team members with specific permissions — for example, letting them manage orders or update products without accessing your store settings or billing.' },
  { q: 'Is there a free trial?', a: 'Yes — every new store starts with a 30-day free trial of all features. No card needed to begin.' },
  { q: 'Can I change my store name or link later?', a: 'Anytime. Your QR code regenerates automatically to match.' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const go = () => navigate('/vendor/auth');

  return (
    <>
      <Helmet>
        <title>SAANJH — Launch your QR-powered online store in minutes</title>
        <meta name="description" content="SAANJH turns any small business into a beautiful online store with its own QR code. Customize your storefront, add products, share your QR — and take orders from any phone. 30-day free trial, from ₹123/month." />
        <meta name="keywords" content="online store india, QR code store, restaurant menu online, cafe ordering, boutique online store, small business website, no-code store, ludhiana" />
        <link rel="canonical" href="https://saanjh.cc/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SAANJH" />
        <meta property="og:title" content="SAANJH — Launch your QR-powered online store in minutes" />
        <meta property="og:description" content="A beautiful online store + your own QR code. Take orders from any phone. 30-day free trial." />
        <meta property="og:url" content="https://saanjh.cc/" />
        <meta property="og:image" content="https://saanjh.cc/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SAANJH — Launch your QR-powered online store in minutes" />
        <meta name="twitter:description" content="A beautiful online store + your own QR code. Take orders from any phone. 30-day free trial." />
        <meta name="twitter:image" content="https://saanjh.cc/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'SAANJH',
          url: 'https://saanjh.cc/',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://saanjh.cc/store?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        })}</script>
      </Helmet>

      <div className="min-h-screen overflow-x-hidden bg-slate-950 text-slate-100">

        {/* Header */}
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="inline-block h-7 w-7 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500" />
              <span className="text-xl font-extrabold tracking-tight">SAANJH</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button onClick={go} className="text-sm text-slate-300 hover:text-white transition-colors px-2 py-2">Vendor login</button>
              <Button onClick={go} className="rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-9 px-4 text-sm" data-testid="get-started-btn">
                Get started
              </Button>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative overflow-hidden">
          {/* colour blobs */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute -top-24 -left-20 h-80 w-80 rounded-full bg-purple-600/30 blur-3xl" />
            <div className="absolute top-10 right-0 h-72 w-72 rounded-full bg-pink-600/30 blur-3xl" />
            <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-indigo-600/30 blur-3xl" />
          </div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-16 sm:pb-20 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs sm:text-sm font-medium text-purple-200">
              <Sparkles className="h-4 w-4" /> Your store + your QR code, in minutes
            </span>
            <h1 className="mt-6 text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Turn your business into a
              <span className="block bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 bg-clip-text text-transparent">
                beautiful online store.
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              SAANJH gives every café, kitchen and boutique its own branded storefront and QR code.
              Add your products, share your code, and take orders from any phone — no website, no app, no code.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button onClick={go} size="lg" className="w-full sm:w-auto rounded-full h-12 px-7 text-base bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-900/40" data-testid="hero-cta-btn">
                Start your free store <ArrowRight className="h-5 w-5 ml-1" />
              </Button>
              <a href="/store/timbercafe" className="w-full sm:w-auto">
                <Button variant="outline" size="lg" className="w-full rounded-full h-12 px-7 text-base bg-white/5 border-white/20 text-white hover:bg-white/10">
                  See a live store
                </Button>
              </a>
            </div>
            <p className="mt-4 text-xs text-slate-400">30-day free trial · No card to start · Live in minutes</p>
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">From signup to first order in 3 steps</h2>
            <p className="mt-3 text-slate-300">No tech team required — really.</p>
          </div>
          <div className="grid gap-5 sm:gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-lg font-extrabold">{s.n}</div>
                <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Everything you need to sell</h2>
            <p className="mt-3 text-slate-300 max-w-2xl mx-auto">A complete storefront, your QR code, and simple tools — built mobile-first for you and your customers.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all hover:bg-white/[0.07] hover:-translate-y-1">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500/90 to-pink-500/90 flex items-center justify-center shadow-lg shadow-purple-900/30">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-300 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
          {/* customer highlight */}
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-purple-600/15 to-pink-600/15 p-6 flex gap-4">
              <Smartphone className="h-8 w-8 shrink-0 text-purple-300" />
              <div><h3 className="text-lg font-bold">Frictionless for shoppers</h3><p className="mt-1 text-sm text-slate-300">Scan, browse, add to cart, done — right in the browser. No app, no signup.</p></div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-600/15 to-purple-600/15 p-6 flex gap-4">
              <ShieldCheck className="h-8 w-8 shrink-0 text-indigo-300" />
              <div><h3 className="text-lg font-bold">Secure guest checkout</h3><p className="mt-1 text-sm text-slate-300">Orders are verified with a one-time email code — safe, fast, and password-free.</p></div>
            </div>
          </div>
        </section>

        {/* Built for */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Made for local businesses</h2>
            <p className="mt-3 text-slate-300">If you sell something, SAANJH gives you a storefront.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-2.5">
              {BUILT_FOR.map((b) => (
                <span key={b} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-slate-200">{b}</span>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section id="pricing" className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Simple, honest pricing</h2>
            <p className="mt-3 text-slate-300">Start free for 30 days. All features included on every plan.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 max-w-3xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7">
              <p className="text-sm font-semibold text-purple-300">Monthly</p>
              <p className="mt-2 text-4xl font-extrabold">₹123<span className="text-base font-medium text-slate-400">/month</span></p>
              <p className="mt-1 text-sm text-slate-400">Billed monthly. Cancel anytime.</p>
              <PlanList />
              <Button onClick={go} className="mt-6 w-full rounded-full bg-white/10 hover:bg-white/15 border border-white/15">Start free trial</Button>
            </div>
            <div className="relative rounded-2xl border border-purple-400/40 bg-gradient-to-br from-purple-600/20 to-pink-600/20 p-7">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 px-3 py-1 text-xs font-bold">SAVE ~25%</span>
              <p className="text-sm font-semibold text-pink-200">Annual</p>
              <p className="mt-2 text-4xl font-extrabold">₹1,113<span className="text-base font-medium text-slate-300">/year</span></p>
              <p className="mt-1 text-sm text-slate-300">Just ₹93/month, billed yearly.</p>
              <PlanList />
              <Button onClick={go} className="mt-6 w-full rounded-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">Start free trial</Button>
            </div>
          </div>
          <p className="mt-5 text-center text-xs text-slate-500">Subscription billing is handled securely via Razorpay.</p>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-center mb-8">Questions, answered</h2>
          <div className="space-y-3">
            {FAQ.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <summary className="cursor-pointer list-none flex items-center justify-between font-semibold">
                  {f.q}
                  <span className="ml-3 text-purple-300 transition-transform group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 to-pink-600 p-10 sm:p-14 text-center">
            <div className="pointer-events-none absolute -top-16 -right-10 h-56 w-56 rounded-full bg-white/15 blur-2xl" />
            <h2 className="relative text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Ready to open your store?</h2>
            <p className="relative mt-3 text-white/90 max-w-xl mx-auto">Set it up today, share your QR tonight, take orders tomorrow.</p>
            <Button onClick={go} size="lg" className="relative mt-7 rounded-full h-12 px-8 text-base bg-white text-purple-700 hover:bg-slate-100 font-semibold" data-testid="footer-cta-btn">
              Create your free store <Zap className="h-5 w-5 ml-1" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="col-span-2 md:col-span-1">
                <div className="flex items-center gap-2 mb-3"><span className="inline-block h-6 w-6 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500" /><span className="text-lg font-extrabold">SAANJH</span></div>
                <p className="text-sm text-slate-400 leading-relaxed">QR-powered online stores for local businesses. Launch in minutes, sell from anywhere.</p>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Product</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                  <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                  <li><button onClick={go} className="hover:text-white transition-colors">Vendor portal</button></li>
                  <li><button onClick={() => navigate('/admin/auth')} className="hover:text-white transition-colors">Admin login</button></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Company</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li><button onClick={() => navigate('/about')} className="hover:text-white transition-colors">About us</button></li>
                  <li><button onClick={() => navigate('/contact')} className="hover:text-white transition-colors">Contact us</button></li>
                  <li><button onClick={() => navigate('/privacy')} className="hover:text-white transition-colors">Privacy policy</button></li>
                  <li><button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms of service</button></li>
                  <li><button onClick={() => navigate('/refund')} className="hover:text-white transition-colors">Return &amp; refund policy</button></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-sm">Contact</h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li>Saanjh, C/O ItsMeHere</li>
                  <li>SBS Nagar, Ludhiana</li>
                  <li>Punjab — 141001</li>
                  <li><a href="tel:+917837640500" className="hover:text-white transition-colors">+91 78376 40500</a></li>
                  <li><a href="mailto:admin@saanjh.cc" className="hover:text-white transition-colors">admin@saanjh.cc</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-white/10 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-slate-500">
              <p>&copy; {new Date().getFullYear()} SAANJH. All rights reserved.</p>
              <p>Payments secured by Razorpay</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

const PlanList = () => (
  <ul className="mt-5 space-y-2.5 text-sm">
    {[
      'Your branded storefront + QR code',
      'Unlimited products & CSV upload',
      'Full theme customization',
      'Custom pages & order alerts',
      'Team & staff access',
      'Flexible payment options (UPI + counter)',
      'WhatsApp alerts (add-on, pay-as-you-go)',
    ].map((t) => (
      <li key={t} className="flex items-start gap-2 text-slate-200">
        <Check className="h-4 w-4 mt-0.5 shrink-0 text-emerald-400" /> {t}
      </li>
    ))}
  </ul>
);

export default LandingPage;
